import {
  DocumentNode,
  gql,
  useQueryWithPreviousData,
} from "@outschool/ui-apollo";
import {
  AttachmentFragment,
  MeetingFragment,
} from "@outschool/ui-components-classroom";

import {
  AccountSectionNewQueryVariables,
  AccountSectionNewQuery as AccountSectionQueryType,
} from "../../shared/generated/graphql";
import ActivitySummary from "../components/enrolling/ActivitySummary";
import { LearnerPrivateNote } from "../components/learner/LearnerPrivateNote";
import SectionManageToolbar from "../components/nav/SectionManageToolbar";
import SectionTeaser from "../components/section/SectionTeaser";
import BaseEnrollmentFragment from "./BaseEnrollmentFragment";
import CharterSchoolFragment from "./CharterSchoolFragment";
import EnrollmentPaymentFragment from "./EnrollmentPaymentFragment";
import EnrollmentPaymentRefundFragment from "./EnrollmentPaymentRefundFragment";
import FeedbackForLeaderFragment from "./FeedbackForLeaderFragment";
import GiftEnrollmentFragment from "./GiftEnrollmentFragment";
import LearnerFragment from "./LearnerFragment";
import { ParentFragment, ParentLeaderFragment } from "./ParentFragments";
import { SectionFragment, SectionLeaderFragment } from "./SectionFragments";

export const AccountSectionQuery = gql`
  query AccountSectionNew(
    $uid: ID!
    $learnerOrEnrollmentUid: String!
    $hasSelection: Boolean!
  ) {
    section(uid: $uid) {
      filledSpaceCount
      ...SectionFragment
      ...SectionLeaderFragment
      ...SectionManageToolbar_section
      meetings {
        ...MeetingFragment
        refundableAmountCents
        paidAt
      }
      currentUserGiftEnrollments {
        ...GiftEnrollmentFragment
      }
      activity {
        uid
        title
        price_cents
        ...ActivitySummary_activity
        ...SectionManageToolbar_activity
      }
      leader {
        uid
        name
        is_crosslister
      }
      feedbacks: feedbacksForLeader {
        ...FeedbackForLeaderFragment
        buyer: buyerNew {
          ...ParentLeaderFragment
        }
      }
      enrollments {
        uid
        learner {
          uid
          name
        }
      }
      selectedEnrollment(learnerOrEnrollmentUid: $learnerOrEnrollmentUid)
        @include(if: $hasSelection) {
        ...BaseEnrollmentFragment
        ... on BaseEnrollment {
          learner {
            ...LearnerPrivateNote_learner
          }
          parent {
            ...ParentFragment
            hasSubscription
            canPayWithSubscriptionCredits
          }
          hasPaymentSchedule
        }
        ... on Enrollment {
          uid
          isFirstOutschoolClass
          isRepeatLearnerOfTeacher
          section_uid
          created_at
          confirmed_at
          withdrawn_at
          ongoing_stopped_at
          ongoing_start_time
          ongoing_end_time
          price_cents
          usersCanCommunicate
          purchasedAsGift
          siblings {
            learner {
              ...LearnerFragment
            }
            currentOrLastSection {
              filledSpaceCount
              ...SectionFragment
            }
          }
          otherSections {
            uid
            filledSpaceCount
            ...SectionFragment
            ...SectionTeaser_section
          }
          paymentSchedule {
            uid
          }
          clubVerificationVideo {
            ...AttachmentFragment
          }
          payments {
            uid
            unrefundedCredits
          }
        }
        parent {
          uid
          hasSubscription
          canPayWithSubscriptionCredits
          ...ParentFragment
        }
        ... on GiftEnrollment {
          withdrawn_at
          ongoing_stopped_at
          ongoing_start_time
          ongoing_end_time
          giftClaimedAt
          currentUserIsPurchaser
          paymentSchedule {
            uid
          }
          payments {
            uid
            unrefundedCredits
          }
        }
        pauseWindow {
          uid
          startDate
          endDate
        }
        isPaused
        isEnded
      }
      learnerEnrollmentPayments(learnerUid: $learnerOrEnrollmentUid)
        @include(if: $hasSelection) {
        ...EnrollmentPaymentFragment
        amount_credits
        unrefundedCredits
        credit_account_type
        oneOnOneMeeting {
          uid
          start_time
        }
        refunds {
          ...EnrollmentPaymentRefundFragment
          amount_credits
        }
        organization {
          ...CharterSchoolFragment
        }
      }
    }
  }
  ${SectionFragment}
  ${SectionLeaderFragment}
  ${SectionManageToolbar.fragments.section}
  ${SectionManageToolbar.fragments.activity}
  ${SectionTeaser.fragments.section}
  ${ActivitySummary.fragments.activity}
  ${MeetingFragment}
  ${GiftEnrollmentFragment}
  ${LearnerFragment}
  ${CharterSchoolFragment}
  ${BaseEnrollmentFragment}
  ${EnrollmentPaymentFragment}
  ${EnrollmentPaymentRefundFragment}
  ${FeedbackForLeaderFragment}
  ${ParentFragment}
  ${ParentLeaderFragment}
  ${LearnerPrivateNote.fragments.learner}
  ${AttachmentFragment}
`;

// new hook which has the same use-cases as graphql hoc before
export function useAccountSectionQuery({
  variables,
  skip,
}: {
  variables: AccountSectionNewQueryVariables;
  skip?: boolean;
}) {
  const { loading, error, data } = useQueryWithPreviousData<
    AccountSectionQueryType,
    AccountSectionNewQueryVariables
  >(AccountSectionQuery, {
    fetchPolicy: "cache-and-network",
    variables,
    skip,
  });
  const section = data && data.section;
  const activity = section && section.activity;
  return {
    loading,
    error,
    section,
    activity,
    data,
  };
}

export type AccountSectionQueryAndVariables = {
  query: DocumentNode;
  variables: AccountSectionNewQueryVariables;
};
